import React, { useState, useEffect } from 'react';
import { useWallet } from '../utils/walletContext';
import './HowToGuide.css';

function HowToGuide() {
  const { isConnected, networkName } = useWallet();
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [walletStatus, setWalletStatus] = useState({
    connected: false,
    network: ''
  });
  
  // Update wallet status only when isConnected or networkName changes
  // Using a ref to prevent infinite updates
  const isFirstRender = React.useRef(true);
  
  useEffect(() => {
    if (isFirstRender.current) {
      // Set initial value on first render
      setWalletStatus({
        connected: isConnected,
        network: networkName
      });
      isFirstRender.current = false;
      return;
    }
    
    // Only update if values actually changed
    if (walletStatus.connected !== isConnected || 
        walletStatus.network !== networkName) {
      setWalletStatus({
        connected: isConnected,
        network: networkName
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, networkName]);

  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Define section content rendering function
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="section-content">
            <h3>Overview of Civil Claims Case Registry</h3>
            <p>
              Welcome to the California Civil Claims Court Web3 Integration. This application provides a decentralized approach to managing civil claims cases using blockchain technology for secure, transparent record-keeping.
            </p>
            <div className="feature-grid">
              <div className="feature-card">
                <div className="feature-icon">📋</div>
                <h4>Case Registration</h4>
                <p>File and register civil claims cases on the blockchain with secure IPFS document storage</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h4>Case Tracking</h4>
                <p>Monitor case status and updates through the blockchain registry</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🔒</div>
                <h4>Secure Evidence</h4>
                <p>Store and manage evidence with optional encryption for sensitive information</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">⚖️</div>
                <h4>Role-Based Access</h4>
                <p>Different permissions for plaintiffs, defendants, court officials, and administrators</p>
              </div>
            </div>
            <div className="alert-box info">
              <p><strong>Note:</strong> This application runs on the Base blockchain network. You'll need to connect a compatible wallet like MetaMask to use all features.</p>
            </div>
          </div>
        );
      
      case 'roles':
        return (
          <div className="section-content">
            <h3>Roles and Permissions</h3>
            <p>
              The Civil Claims Case Registry uses a role-based access control system to ensure appropriate permissions for different participants.
            </p>
            
            <div className="role-card">
              <h4>REGISTRAR_ROLE</h4>
              <p><strong>Who:</strong> Court clerks, authorized intake personnel</p>
              <p><strong>Permissions:</strong></p>
              <ul>
                <li>Register new cases in the system</li>
                <li>Create initial case records with IPFS metadata</li>
                <li>Assign plaintiffs to cases</li>
              </ul>
              <p><strong>How to obtain:</strong> Assigned by contract owner or admin (typically court administration)</p>
            </div>
            
            <div className="role-card">
              <h4>COURT_ADMIN_ROLE</h4>
              <p><strong>Who:</strong> Judges, court administrators, legal officials</p>
              <p><strong>Permissions:</strong></p>
              <ul>
                <li>Update case status (e.g., scheduled, in progress, resolved)</li>
                <li>Assign defendants to cases</li>
                <li>Modify fee structures</li>
                <li>Add official case documentation via IPFS</li>
              </ul>
              <p><strong>How to obtain:</strong> Assigned by contract owner based on legal authority</p>
            </div>
            
            <div className="role-card">
              <h4>PAUSER_ROLE</h4>
              <p><strong>Who:</strong> System administrators, emergency response team</p>
              <p><strong>Permissions:</strong></p>
              <ul>
                <li>Pause and unpause contract functionality in case of emergencies</li>
                <li>Temporarily halt case registration if needed</li>
              </ul>
              <p><strong>How to obtain:</strong> Assigned by contract owner to trusted administrators</p>
            </div>
            
            <div className="role-card">
              <h4>Contract Owner</h4>
              <p><strong>Who:</strong> Primary court administration entity</p>
              <p><strong>Permissions:</strong></p>
              <ul>
                <li>Grant and revoke roles from users</li>
                <li>Set fee recipient address</li>
                <li>Withdraw accumulated fees</li>
                <li>Manage overall contract operations</li>
              </ul>
            </div>
            
            <div className="role-card">
              <h4>Case Parties</h4>
              <p><strong>Plaintiff:</strong> The person filing the claim</p>
              <p><strong>Defendant:</strong> The person being claimed against</p>
              <p><strong>Permissions:</strong></p>
              <ul>
                <li>View case information related to their cases</li>
                <li>Submit evidence (through court-approved processes)</li>
                <li>Receive case updates</li>
              </ul>
            </div>
            
            <div className="alert-box warning">
              <p><strong>Important:</strong> Role assignments are managed through the blockchain and can only be modified by authorized administrators with appropriate permissions.</p>
            </div>
          </div>
        );
      
      case 'filing':
        return (
          <div className="section-content">
            <h3>Filing a Civil Claims Case</h3>
            
            <h4 className="step-heading">Step 1: Prepare Required Information</h4>
            <div className="step-content">
              <p>Before filing, gather the following information:</p>
              <ul>
                <li>Your wallet address (will be used as the plaintiff identification)</li>
                <li>Defendant's wallet address (if available, can be added later)</li>
                <li>Case description and details</li>
                <li>Claim amount</li>
                <li>Supporting evidence documents</li>
              </ul>
              <p className="note">Note: You'll need Base ETH to cover filing fees (currently 0.002 ETH).</p>
            </div>
            
            <h4 className="step-heading">Step 2: Connect Your Wallet</h4>
            <div className="step-content">
              <p>Connect your Ethereum wallet (such as MetaMask) to the application:</p>
              <ol>
                <li>Click "Connect Wallet" in the navigation bar</li>
                <li>Select your wallet provider and authorize the connection</li>
                <li>Ensure you're connected to the Base network (the app will prompt you to switch if needed)</li>
                <li>Your address will appear in the navigation bar when successfully connected</li>
              </ol>
              <div className="alert-box info">
                <p>Current network: {isConnected ? networkName : "Not connected"}</p>
              </div>
            </div>
            
            <h4 className="step-heading">Step 3: Create Case JSON Metadata</h4>
            <div className="step-content">
              <p>Prepare your case information in the Civil Claims Form:</p>
              <ol>
                <li>Fill out all required information in the form</li>
                <li>Add supporting documents and evidence</li>
                <li>Choose whether to encrypt sensitive information:</li>
                <ul>
                  <li><strong>Public filing:</strong> All case information will be readable on IPFS</li>
                  <li><strong>Encrypted filing:</strong> Use a secure key to encrypt sensitive details</li>
                </ul>
              </ol>
              <div className="code-block">
                <pre>{`// Example case metadata JSON structure
{
  "caseInformation": {
    "title": "Civil Claims against XYZ Company",
    "description": "Dispute regarding defective product...",
    "claimAmount": "1250.00",
    "filingDate": "2025-04-29T12:34:56Z"
  },
  "parties": {
    "plaintiff": {
      "address": "0x123...abc",
      "name": "John Doe",
      "contact": "john@example.com"
    },
    "defendant": {
      "address": "0xdef...789",
      "name": "XYZ Company",
      "contact": "support@xyzcompany.com"
    }
  },
  "evidence": [
    {
      "type": "receipt",
      "description": "Original purchase receipt",
      "ipfsHash": "QmXyz..."
    },
    {
      "type": "correspondence",
      "description": "Email exchanges with company support",
      "ipfsHash": "QmAbc..."
    }
  ]
}`}</pre>
              </div>
            </div>
            
            <h4 className="step-heading">Step 4: Upload to IPFS</h4>
            <div className="step-content">
              <p>Store your case information securely on IPFS:</p>
              <ol>
                <li>Click "Upload to IPFS" in the form</li>
                <li>If you chose encryption, enter your encryption key (save this key securely!)</li>
                <li>Wait for the upload to complete</li>
                <li>The system will return an IPFS Content Identifier (CID) for your case</li>
              </ol>
              <div className="alert-box warning">
                <p><strong>Important:</strong> If you encrypt your case data, you MUST securely store your encryption key. Loss of this key means permanent loss of access to encrypted information.</p>
              </div>
            </div>
            
            <h4 className="step-heading">Step 5: Register on the Blockchain</h4>
            <div className="step-content">
              <p>Register your case on the blockchain registry:</p>
              <ol>
                <li>Navigate to the Smart Contract Registry section</li>
                <li>Enter the required information:</li>
                <ul>
                  <li>Initial Status: "Filed"</li>
                  <li>Plaintiff Address: Your wallet address</li>
                  <li>Defendant Address (if known)</li>
                  <li>IPFS CID from the previous step</li>
                </ul>
                <li>Click "Register Case"</li>
                <li>Confirm the transaction in your wallet and pay the filing fee</li>
                <li>Wait for blockchain confirmation</li>
              </ol>
              <p>Once confirmed, you'll receive a Case ID that can be used to track your case in the system.</p>
            </div>
            
            <div className="alert-box success">
              <p><strong>Congratulations!</strong> Your case is now filed on the blockchain and has a permanent, tamper-proof record.</p>
            </div>
          </div>
        );
      
      case 'tracking':
        return (
          <div className="section-content">
            <h3>Tracking and Managing Cases</h3>
            
            <h4>Looking Up Case Status</h4>
            <p>You can check the status of any case if you have its Case ID:</p>
            <ol>
              <li>Navigate to the "Smart Contract Registry" section</li>
              <li>Scroll to the "Look Up Case" panel</li>
              <li>Enter the Case ID in the input field</li>
              <li>Click "Look Up Case"</li>
              <li>The system will display current case information:</li>
              <ul>
                <li>Current status</li>
                <li>Plaintiff and defendant addresses</li>
                <li>IPFS metadata URI</li>
              </ul>
            </ol>
            
            <h4>Viewing Case Metadata</h4>
            <p>To view the detailed case information stored on IPFS:</p>
            <ol>
              <li>Look up the case to get its IPFS metadata URI</li>
              <li>Extract the IPFS CID from the URI (format: https://freeweb3.infura-ipfs.io/ipfs/CID)</li>
              <li>If the data is not encrypted, you can view it directly through the IPFS gateway</li>
              <li>If encrypted, you'll need the correct decryption key</li>
            </ol>
            
            <h4>Case Status Updates</h4>
            <p>Case status is updated by court administrators as the case progresses:</p>
            <div className="status-flow">
              <div className="status-item">
                <div className="status-badge">Filed</div>
                <p>Initial state when case is first registered</p>
              </div>
              <div className="status-arrow">→</div>
              <div className="status-item">
                <div className="status-badge">Pending</div>
                <p>Waiting for court processing and scheduling</p>
              </div>
              <div className="status-arrow">→</div>
              <div className="status-item">
                <div className="status-badge">Scheduled</div>
                <p>Court date has been assigned</p>
              </div>
              <div className="status-arrow">→</div>
              <div className="status-item">
                <div className="status-badge">In Progress</div>
                <p>Case is currently being heard</p>
              </div>
              <div className="status-arrow">→</div>
              <div className="status-item">
                <div className="status-badge">Judgment</div>
                <p>Decision has been rendered</p>
              </div>
              <div className="status-arrow">→</div>
              <div className="status-item">
                <div className="status-badge">Closed</div>
                <p>Case is finalized</p>
              </div>
            </div>
            
            <h4>Submitting Additional Evidence</h4>
            <p>If you need to submit additional evidence for an existing case:</p>
            <ol>
              <li>Prepare the evidence documents</li>
              <li>Use the Evidence Manager component to upload to IPFS</li>
              <li>Submit the IPFS CID to the court through established channels</li>
              <li>Court admins can update the case with new evidence links</li>
            </ol>
            <div className="alert-box info">
              <p><strong>Note:</strong> Only court administrators can update the official case record on the blockchain. Contact the court directly if you need to submit additional materials.</p>
            </div>
          </div>
        );
      
      case 'technical':
        return (
          <div className="section-content">
            <h3>Technical Implementation Details</h3>
            
            <h4>Smart Contract Architecture</h4>
            <p>The SmallClaimsCaseRegistry smart contract implements several key features:</p>
            <ul>
              <li><strong>ERC721 Standard:</strong> Each case is represented as a non-fungible token (NFT)</li>
              <li><strong>Role-Based Access Control:</strong> Different permissions for registrars, admins, etc.</li>
              <li><strong>IPFS Integration:</strong> Off-chain storage of case details with on-chain references</li>
              <li><strong>Fee Management:</strong> Handling filing and verification fees</li>
            </ul>
            
            <div className="code-block">
              <pre>{`// Key smart contract functions
// Register a new case
function registerCase(
    address to,
    string memory uri,
    string memory initialStatus,
    address defendant
) public payable nonReentrant onlyRegistrar returns (uint256);

// Update case status
function updateCaseStatus(
    uint256 tokenId,
    string memory newStatus,
    string memory ipfsHash
) public payable nonReentrant onlyCourtAdmin;

// Assign defendant
function assignDefendant(uint256 tokenId, address defendant) 
    public onlyCourtAdmin;

// View functions
function getPlaintiff(uint256 tokenId) public view returns (address);
function getDefendant(uint256 tokenId) public view returns (address);
function getCaseStatus(uint256 tokenId) public view returns (string memory);`}</pre>
            </div>
            
            <h4>IPFS Integration</h4>
            <p>The application uses IPFS (InterPlanetary File System) for decentralized storage:</p>
            <ul>
              <li>Case metadata is stored as JSON on IPFS</li>
              <li>Evidence documents are uploaded to IPFS separately</li>
              <li>The IPFS Content Identifier (CID) is recorded on the blockchain</li>
              <li>Data can be optionally encrypted with AES-256 before upload</li>
            </ul>
            
            <h4>React State Management for Form Data</h4>
            <p>The application uses React's useState hooks to manage form data before submission:</p>
            <div className="code-block">
              <pre>{`// Example of using useState to manage form data
const [formData, setFormData] = useState({
  caseTitle: '',
  description: '',
  claimAmount: '',
  defendantInfo: {
    name: '',
    address: '',
    walletAddress: ''
  },
  evidence: []
});

// Updating form data
const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prevData => ({
    ...prevData,
    [name]: value
  }));
};

// Creating JSON for IPFS upload
const prepareForUpload = () => {
  const timestamp = new Date().toISOString();
  return {
    ...formData,
    filingDate: timestamp,
    plaintiffWallet: account
  };
};

// Encrypting and uploading
const handleEncryptAndUpload = async () => {
  const jsonData = prepareForUpload();
  const encryptedData = encryptJSON(jsonData, encryptionKey);
  const cid = await uploadToIPFS(encryptedData);
  return cid;
};`}</pre>
            </div>
            
            <h4>Encryption Implementation</h4>
            <p>Client-side AES-256 encryption is used to protect sensitive case information:</p>
            <ul>
              <li>Encryption is performed in the browser before uploading to IPFS</li>
              <li>The encryption key is never stored or transmitted to servers</li>
              <li>Users must securely store their encryption keys</li>
              <li>Decryption requires the same key used for encryption</li>
            </ul>
            
            <h4>Blockchain Transaction Flow</h4>
            <p>When registering or updating cases, the following transaction flow occurs:</p>
            <ol>
              <li>Form data is collected and validated</li>
              <li>Data is prepared as JSON and optionally encrypted</li>
              <li>JSON is uploaded to IPFS, returning a CID</li>
              <li>A blockchain transaction is prepared with the IPFS CID</li>
              <li>User confirms the transaction in their wallet</li>
              <li>Transaction is submitted to the Base blockchain</li>
              <li>Smart contract processes the transaction and updates state</li>
              <li>Events are emitted for case registration or updates</li>
              <li>UI is updated with transaction status and results</li>
            </ol>
          </div>
        );
      
      case 'faq':
        return (
          <div className="section-content">
            <h3>Frequently Asked Questions</h3>
            
            <div className="faq-list">
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq1'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq1')}
                >
                  <h4>What is the advantage of using blockchain for civil claims cases?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq1'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq1'] && (
                  <div className="faq-answer">
                    <p>Blockchain provides several key advantages for civil claims case management:</p>
                    <ul>
                      <li><strong>Immutability:</strong> Case records cannot be altered once created, ensuring an authentic history</li>
                      <li><strong>Transparency:</strong> All parties can verify case status and history</li>
                      <li><strong>Accessibility:</strong> Case information can be accessed remotely at any time</li>
                      <li><strong>Security:</strong> Cryptographic security protects case integrity</li>
                      <li><strong>Efficiency:</strong> Reduced paperwork and automated processes</li>
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq2'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq2')}
                >
                  <h4>Who can see my case information?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq2'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq2'] && (
                  <div className="faq-answer">
                    <p>Case visibility depends on your chosen privacy settings:</p>
                    <ul>
                      <li><strong>Public filing:</strong> Anyone with your case ID can view basic case information. Detailed metadata on IPFS is publicly accessible.</li>
                      <li><strong>Encrypted filing:</strong> Basic case information (status, parties) is visible on the blockchain, but detailed information is encrypted and only accessible with the decryption key.</li>
                    </ul>
                    <p>The court system always maintains access to case information through appropriate administrative channels, similar to traditional court records.</p>
                  </div>
                )}
              </div>
              
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq3'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq3')}
                >
                  <h4>What if I lose my encryption key?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq3'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq3'] && (
                  <div className="faq-answer">
                    <p>If you lose your encryption key:</p>
                    <ul>
                      <li>There is no way to recover encrypted data without the original key</li>
                      <li>The court maintains official records of essential case information</li>
                      <li>You may need to re-submit information to the court through official channels</li>
                      <li>We strongly recommend storing encryption keys in a secure password manager</li>
                    </ul>
                    <p>To prevent key loss, consider using a secure password manager or writing down your key and storing it in a safe location.</p>
                  </div>
                )}
              </div>
              
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq4'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq4')}
                >
                  <h4>How do I get the REGISTRAR_ROLE or COURT_ADMIN_ROLE?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq4'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq4'] && (
                  <div className="faq-answer">
                    <p>These roles are assigned by the court administration:</p>
                    <ul>
                      <li>REGISTRAR_ROLE is typically given to court clerks and authorized staff</li>
                      <li>COURT_ADMIN_ROLE is assigned to judges and court administrators</li>
                      <li>Roles are granted based on official position and responsibilities</li>
                      <li>Contact your court administrator if you believe you should have a specific role</li>
                    </ul>
                    <p>Regular users (plaintiffs and defendants) do not need special roles to interact with their own cases.</p>
                  </div>
                )}
              </div>
              
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq5'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq5')}
                >
                  <h4>What fees are involved in using this system?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq5'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq5'] && (
                  <div className="faq-answer">
                    <p>There are two types of fees in the system:</p>
                    <ol>
                      <li><strong>Filing Fee:</strong> Currently 0.002 ETH, paid when registering a new case</li>
                      <li><strong>Verification Fee:</strong> Currently 0.0002 ETH, paid when updating case status</li>
                    </ol>
                    <p>Additionally, you'll need to pay blockchain gas fees, which vary based on network congestion. The Base network typically has relatively low gas fees compared to Ethereum mainnet.</p>
                    <p>Note that these blockchain fees are separate from any administrative fees required by the court itself.</p>
                  </div>
                )}
              </div>
              
              <div className="faq-item">
                <div 
                  className={`faq-question ${expandedFaqs['faq6'] ? 'expanded' : ''}`}
                  onClick={() => toggleFaq('faq6')}
                >
                  <h4>Is this system legally binding?</h4>
                  <span className="toggle-icon">{expandedFaqs['faq6'] ? '−' : '+'}</span>
                </div>
                {expandedFaqs['faq6'] && (
                  <div className="faq-answer">
                    <p>The blockchain registry serves as an official record-keeping system for the court, but the legal process still follows established California civil claims court procedures:</p>
                    <ul>
                      <li>The blockchain provides a secure, tamper-proof record of case information</li>
                      <li>Actual legal proceedings follow standard court procedures</li>
                      <li>Judgments are legally binding as determined by the court</li>
                      <li>The system is an enhancement to existing legal processes, not a replacement</li>
                    </ul>
                    <p>Always follow court instructions and official notices in addition to monitoring the blockchain registry.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
    
      default:
        return <div>Select a section to learn more</div>;
    }
  };

  return (
    <div className="how-to-guide">
      <div className="guide-header">
        <h1>Civil Claims Case Registry Guide</h1>
        <p>Learn how to use the blockchain-based civil claims court system</p>
      </div>
      
      <div className="guide-container">
        <div className="guide-sidebar">
          <ul className="guide-nav">
            <li 
              className={activeSection === 'overview' ? 'active' : ''}
              onClick={() => setActiveSection('overview')}
            >
              System Overview
            </li>
            <li 
              className={activeSection === 'roles' ? 'active' : ''}
              onClick={() => setActiveSection('roles')}
            >
              Roles & Permissions
            </li>
            <li 
              className={activeSection === 'filing' ? 'active' : ''}
              onClick={() => setActiveSection('filing')}
            >
              Filing a Case
            </li>
            <li 
              className={activeSection === 'tracking' ? 'active' : ''}
              onClick={() => setActiveSection('tracking')}
            >
              Tracking & Managing Cases
            </li>
            <li 
              className={activeSection === 'technical' ? 'active' : ''}
              onClick={() => setActiveSection('technical')}
            >
              Technical Details
            </li>
            <li 
              className={activeSection === 'faq' ? 'active' : ''}
              onClick={() => setActiveSection('faq')}
            >
              FAQ
            </li>
          </ul>
          
          <div className="wallet-status">
            <h4>Wallet Status</h4>
            <div className={`status-indicator ${walletStatus.connected ? 'connected' : 'disconnected'}`}>
              {walletStatus.connected ? 'Connected' : 'Not Connected'}
            </div>
            {walletStatus.connected && (
              <div className="network-info">
                Network: {walletStatus.network}
              </div>
            )}
          </div>
        </div>
        
        <div className="guide-content">
          {renderSectionContent()}
        </div>
      </div>
    </div>
  );
}

export default HowToGuide;